/**
 * Routes of area (c) — operations. OWNED BY area (c) — operations. The paths are the foundation's (boards.ts hrefs
 * point at them). Windows are UI state in the address (`?fenetre=`), so a board href opens the screen with its window.
 */
import { lazy } from "react";
import { createRoute } from "@tanstack/react-router";
import { z } from "zod";
import { guard, staffRoute } from "@/app/route-base";

const TransactionsList = lazy(() => import("./transactions/list"));
const TransactionsDetail = lazy(() => import("./transactions/detail"));
const Taxes = lazy(() => import("./taxes/screen"));
const Documents = lazy(() => import("./documents/screen"));
const Social = lazy(() => import("./social/screen"));
const LedgerClients = lazy(() => import("./ledger/clients"));
const Ledger = lazy(() => import("./ledger/screen"));

const opt = <T extends z.ZodType>(s: T) => s.optional().catch(undefined);

export const TransactionsDetailSearch = z.object({
  fenetre: opt(z.enum(["piece", "invalider", "demander", "filtres"])),
  transaction: opt(z.string().min(1)),
  compte: opt(z.string().min(1)),
});
export type TransactionsDetailSearch = z.infer<typeof TransactionsDetailSearch>;

export const TaxesSearch = z.object({
  fenetre: opt(z.enum(["attacher", "finaliser", "filtres"])),
  declaration: opt(z.string().min(1)),
});
export type TaxesSearch = z.infer<typeof TaxesSearch>;

export const DocumentsSearch = z.object({
  client: opt(z.string().min(1)),
  dossier: opt(z.string().min(1)),
  fenetre: opt(z.enum(["deposer", "nouveau-dossier", "filtres"])),
});
export type DocumentsSearch = z.infer<typeof DocumentsSearch>;

export const SocialSearch = z.object({
  client: opt(z.string().min(1)),
  fenetre: opt(z.enum(["deposer", "envoyer"])),
});
export type SocialSearch = z.infer<typeof SocialSearch>;

/** Admin-Transactions */
export const comptaTransactionsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/transactions",
  validateSearch: z.object({}),
  beforeLoad: guard("cabinet.operations", "read"),
  component: TransactionsList,
});

/** Admin-Transactions-Detail, Admin-Transactions-PJ (?fenetre=piece), Admin-Transactions-Invalider (?fenetre=invalider) */
export const comptaTransactionsCompanyidRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/transactions/$companyId",
  validateSearch: TransactionsDetailSearch,
  beforeLoad: guard("cabinet.operations", "read"),
  component: TransactionsDetail,
});

/** Admin-Impots */
export const comptaImpotsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/impots",
  validateSearch: TaxesSearch,
  beforeLoad: guard("cabinet.operations", "read"),
  component: Taxes,
});

/** Admin-Impots-TVA, Admin-Impots-IS, Admin-Impots-CFE, Admin-Impots-TVS: the inbox with the tax window open. */
export const comptaImpotsCompanyidTaxRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/impots/$companyId/$tax",
  validateSearch: TaxesSearch,
  beforeLoad: guard("cabinet.operations", "read"),
  component: Taxes,
});

/** Admin-Documents */
export const comptaDocumentsRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/documents",
  validateSearch: DocumentsSearch,
  beforeLoad: guard("cabinet.operations", "read"),
  component: Documents,
});

/** Admin-Social */
export const comptaSocialRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/social",
  validateSearch: SocialSearch,
  beforeLoad: guard("cabinet.operations", "read"),
  component: Social,
});

/** Écritures (no board): the firm's clients, then one client's journals. */
export const comptaEcrituresRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/ecritures",
  validateSearch: z.object({}),
  beforeLoad: guard("cabinet.operations", "read"),
  component: LedgerClients,
});

export const comptaEcrituresCompanyidRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/compta/ecritures/$companyId",
  validateSearch: z.object({}),
  beforeLoad: guard("cabinet.operations", "read"),
  component: Ledger,
});

export const operationsRoutes = [
  comptaTransactionsRoute,
  comptaTransactionsCompanyidRoute,
  comptaImpotsRoute,
  comptaImpotsCompanyidTaxRoute,
  comptaDocumentsRoute,
  comptaSocialRoute,
  comptaEcrituresRoute,
  comptaEcrituresCompanyidRoute,
] as const;
